//import core libraries
import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";

//import third party libraries
import socketIO from "socket.io-client";
import { useMediaQuery } from "react-responsive";
import { twMerge } from "tailwind-merge";
import { IoSearch } from "react-icons/io5";
import { IoClose } from "react-icons/io5";
import { FaRegMessage } from "react-icons/fa6";
import { HiMenu } from "react-icons/hi";
import { IoMdArrowRoundBack } from "react-icons/io";

//importing custom hooks
import { useThunk } from "../../hooks/useThunk.js";

// import constant data
import { orderStatus, roles } from "../../constants/commonConstants.js";

//import assets
import logo from "../../assets/Sidebar/Logo_short.png";
import waiting from "../../assets/Thread/waiting.svg";
import waiting_two from "../../assets/Thread/waiting2.svg";
import ClipLoader from "react-spinners/ClipLoader";

//import components
import TutorialCard from "../../components/Thread/TutorialCard";
import ArrowButton from "../../components/Form/ArrowButton";
import MessageList from "../../components/Thread/MessageList.js";
import CountdownTimer from "../../components/Thread/CountDownTimer.jsx";
import OrderDetails from "../../components/Thread/OrderDetails.jsx";
import TrackOrder from "../../components/Thread/TrackOrder.jsx";
import FinalSubmission from "../../components/Thread/FinalSubmission.jsx";

//import dispatchers, thunks, functions, utills
import {
  clearThreads,
  setSelectedThreadId,
  setSelectedThreadRoomId,
  setSelectedFormType,
  setSelectedThread,
  setSelectedOrder,
  clearSelectedThreadAndOrder,
  setThreads,
  setSelectedPage,
  fetchUserThreads,
  fetchUserOrderByRoomId,
  clearThreadChats,
  fetchThreadById,
  setBulkThreads,
  setThreadBulkChats,
} from "../../store";
import { getTokenPayload } from "../../utils/getTokenPayload";
import showToast from "../../utils/toastNotifications.js";
import { getUnreadMessagesCount } from "../../utils/getUnreadMessagesCount.js";
import { useNavigate } from "react-router-dom";

// extracrting current environment and it's properties
import { CONFIGURATIONS } from "../../config/envConfig.js";
import { currencyFormatter, formatOrderStatus } from "../../utils/formatting.js";
import { formatYYYY_MM_DD_HH_MM_A } from "../../utils/dateFormating.js";

// client socket
const socket = socketIO.connect(CONFIGURATIONS.API_BASE_DOMAIN, {
  auth: {
    token: localStorage.getItem("token"),
  },
});

// const values
const SIZE = 5;

const Thread = () => {

  const tokenPayload = getTokenPayload();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams(); //param1 = searchParams.get('param1');
  const thread = useSelector((state) => state.thread);
  const { orders } = useSelector((state) => state.createOrder?.userOrders);

  const roomIdSearchParam = searchParams.get("roomIdParam") || null;
  const threadIdSearchParam = searchParams.get("threadIdParam") || null;

  //thread pagination model
  const [pagination, setPagination] = useState({
    page: Math.floor(thread.threads?.length / SIZE + 1) || 1,
    size: SIZE,
  });
  const [totalNumOfRecords, setTotalNumOfRecords] = useState(0);

  //states for manage dynamic screen behaviours
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);
  const isLargeScreen = useMediaQuery({ query: "(min-width: 1024px)" });
  const [isScreenSmall, setIsScreenSmall] = useState(window.innerWidth >= 360);
  const [isScreenMedium, setIsScreenMedium] = useState(
    window.innerWidth >= 640
  );
  const [isScreenLarge, setIsScreenLarge] = useState(window.innerWidth >= 1440);

  /*
   * ============================= CHATTING RELATED REFS =========================
   *
   * definig several refes parallay to several local/global states due to socket listeners not getting the latest updated
   *  values of states and resulting unpredicte behaviours
   */

  const userIdRef = useRef(tokenPayload?._id || "");
  const roleRef = useRef(
    tokenPayload?.roles ? tokenPayload?.roles[0] : roles.USER
  );
  const selectedThreadRef = useRef(thread?.selectedThread);
  const threasdsRef = useRef(thread?.threads);
  const chatRef = useRef(thread?.selectedThreadChat);
  // const isTypingRef = useRef(isTyping);
  // const typingUsersRef = useRef(typingUsers);
  // const mentionUsersRef = useRef(mentionUsers);
  // const selectedMentionUserRef = useRef(selectedMentionUser);
  // const currentMentionedUsersRef = useRef(currentMentionedUsers);

  //invoking custom hooks
  const [doFetchUserThreads, isFetchingUserThreads, fetchUserThreadsError] =
    useThunk(fetchUserThreads);
  const [
    doFetchUserOrderByRoomId,
    isFetchUserOrderByRoomId,
    fetchUserOrderByRoomIdError,
  ] = useThunk(fetchUserOrderByRoomId);
  const [doFetchThreadById, isFetchThreadById, fetchThreadByIdError] =
    useThunk(fetchThreadById);

  /*
  *
  * =====================  SIDE EFFECTS DEFINITIONS  =============================================================================
  *
  */

  // defining event listeners
  useEffect(() => {
    //handle screen size resize event listeners
    const handleResize = () => {
      setIsScreenLarge(window.innerWidth >= 1440);
      setIsScreenMedium(window.innerWidth >= 640);
      setIsScreenSmall(window.innerWidth >= 360);
    };

    //clear redux storage - threads properties
    const handleUnload = () => {

      // disconnectiong the socket connection before leave from the page/tab/browser
      if (socket.connected) {
        socket.disconnect();
      }
      dispatch(clearThreads());
      dispatch(clearSelectedThreadAndOrder());
    };

    // invoking methods based on if query parameters exists or if query parametrs does not exist
    // query paramerts exists if this thread page loaded from my order table : view order selected
    // query parametrs does not exist if this thread page loaded as general option
    if (roomIdSearchParam && threadIdSearchParam) {
      refetchSelectedThreadAndOrderFromQueryParams();
    } else {
      refetchSelectedThreadAndOrder();
    }

    // triggered when user leaves/close browser, tab or navigates away
    window.addEventListener("beforeunload", handleUnload);
    // detect screen sizes
    window.addEventListener("resize", handleResize);

    // cleanup functions
    return () => {
      window.removeEventListener("beforeunload", handleUnload);
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // fetch user threads accroding to paginatin modal
  useEffect(() => {
    // invoking for fetch user threads
    if (
      thread.threads?.length % SIZE === 0 &&
      !thread?.selectedPage &&
      !roomIdSearchParam &&
      !threadIdSearchParam
    ) {
      fetchAllUserThreads();
    }
  }, [pagination]);

  /*
   *
   * =====================  CHATTING RELATED SIDE EFFECTS DEFINITIONS  =============================================================================
   *
   */

  // client socket join with their each associated rooms
  useEffect(() => {

    // Explicitly connect the socket
    socket.auth = { token: localStorage.getItem('token') };
    socket.connect();

    if (
      (thread?.threads?.length > 0 ||
        (roomIdSearchParam && threadIdSearchParam)) &&
      tokenPayload?._id != ""
    ) {
      socket.emit("join-room", {
        userId: tokenPayload?._id || "",
        role: tokenPayload?.roles ? tokenPayload?.roles[0] : "",
      });
    }

    socket.on("joinRoomResponse", () => { });

    return () => {
      socket.off("joinRoomResponse");
    };
  }, [thread?.threads, roomIdSearchParam, threadIdSearchParam]);

  // emiting events for track who are the active viewing users of threads at this moment
  useEffect(() => {
    if (thread?.selectedThread) {
      socket.emit("open-thread", {
        userId: tokenPayload?._id || "",
        roomId: thread?.selectedThread?._id || "",
      });

      // Handler for closing thread on refresh or tab/browser close
      const handleUnload = () => {
        socket.emit("close-thread", {
          userId: tokenPayload?._id || "",
          roomId: thread?.selectedThread?._id || "",
        });
      };

      // Listen to beforeunload event for cleanup on refresh or close
      window.addEventListener("beforeunload", handleUnload);

      // Cleanup
      return () => {
        window.removeEventListener("beforeunload", handleUnload);
        socket.emit("close-thread", {
          userId: tokenPayload?._id || "",
          roomId: thread?.selectedThread?._id || "",
        });
      };
    }
  }, [thread?.selectedThread]);

  useEffect(() => {
    if( thread?.selectedFormType === "system" && thread?.selectedThread?._id){
      socket.emit("seen-notifications", thread?.selectedThread?._id)
    }
  },[thread?.selectedThread, thread?.selectedFormType])


  // client socket listening for incoming messages or errors
  useEffect(() => {
    socket.on('messageResponse', (data) => {
      appendMessageToRelatedChat(data);
    });

    socket.on("error", (error) => {
      console.log("Server error:", error);
      showToast("error", error || "Error Occurred");
    });

    return () => {
      socket.off("messageResponse");
      socket.off("error");
    };
  }, [socket]);

  /*
   *
   * =====================  CHATTING RELATED FUNCTION DEFINITIONS  =============================================================================
   *
   */

  // method to be used to swap the threads to top of the threads list when it getting new incoming messages & increment unread count locally
  const swapThreads = (prevThreads, roomId, userId) => {
    return new Promise(async (resolve, reject) => {
      let targetIndex = -1;
      let prev = prevThreads ? prevThreads : [];

      for (let i = 0; i < prev.length; i++) {
        if (prev[i]?._id?.toString() === roomId?.toString()) {
          targetIndex = i;
          break;
        }
      }

      // targetIndex not null means, the thread exist locally which is related to incoming recent message
      if (targetIndex > -1) {
        for (let j = targetIndex; j > 0; j--) {
          let temp = prev[j];
          prev[j] = prev[j - 1];
          prev[j - 1] = temp;
        }
      } else {
        let res = await doFetchThreadById({ roomId: roomId });

        // Using the splice() method to remove from the last nth index for n elements
        prev.splice(prev.length - 1, 1);

        if (res.success) {
          prev = [res.response.data, ...prev];
        } else {
          showToast("error", res?.error?.message || "Error Occurred!");
        }
      }
      return resolve([...prev]);
    });
  };

  // method to be used to append incoming message to related chat - as well as update the currently open chats if any
  const appendMessageToRelatedChat = async (newMessage) => {

    let threadPrev = selectedThreadRef.current;
    let chatPrev = chatRef.current;
    let prevUserId = userIdRef.current;

    //update the open chat if any
    if (newMessage?.roomId === threadPrev?._id) {
      dispatch(setThreadBulkChats([...chatPrev, { sender: newMessage?.userId, content: newMessage?.text, attachments: newMessage?.attachments || [], createdAt: newMessage?.createdAt, isAdmin: newMessage?.isAdmin || false }]));
      chatRef.current = [...chatPrev, { sender: newMessage?.userId, content: newMessage?.text, attachments: newMessage?.attachments || [], createdAt: newMessage?.createdAt, isAdmin: newMessage?.isAdmin || false }];
    }

    //update the last message of matching thread and locally increment the incoming unread message count      
    let updatedThreads = threasdsRef.current.map((thread) => {
      if (newMessage?.roomId === thread?._id) {
        return {
          ...thread,
          lastMessage: { sender: newMessage?.userId, content: newMessage?.text, attachments: [] },
          lastRead: thread?.lastRead?.map((item) => {
            if (item?.userId?.toString() === prevUserId?.toString() && thread?._id?.toString() !== threadPrev?._id?.toString()) {
              return { ...item, unreadCount: parseInt(item.unreadCount) + 1 };
            }
            return item;
          })
        };
      }
      return thread;
    });

    const w = await swapThreads(updatedThreads, newMessage?.roomId?.toString(), prevUserId);
    dispatch(setBulkThreads(w));
    threasdsRef.current = w;
    return w
  }

  // ================> Toggle functions <=======================

  // triggered when clicked on a thread located at left side bar
  const handleCardClick = async (threadParam) => {
    try {
      if (
        thread?.selectedFormType !== "message" &&
        thread?.selectedFormType !== "system"
      ) {
        dispatch(setSelectedFormType("message"));
      }
      dispatch(setSelectedThreadId(threadParam?._id));
      dispatch(setSelectedThreadRoomId(threadParam?.roomId));
      dispatch(setSelectedPage(pagination.page));
      // need t fetch the associated order with room id
      if (thread?.selectedThreadRoomId !== threadParam?.roomId) {
        dispatch(clearThreadChats());
        const res = await doFetchUserOrderByRoomId({
          roomId: threadParam?.roomId || "",
        });
        if (!res?.success) {
          showToast(
            "error",
            res?.error?.message || "Error occurred during fetching Data"
          );
        } else {
          dispatch(setSelectedFormType(res?.response?.data?.status === orderStatus.PENDING_PAYMENT ? "system" : "message"));
        }
      }
    } catch (error) {
      showToast("error", "Error occurred during fetching Data");
    }

    dispatch(setSelectedThread(threadParam));
    selectedThreadRef.current = threadParam;
  };

  const handleButtonClick = (formType) => {
    dispatch(setSelectedFormType(formType));
  };

  const toggleSection = () => {
    setIsExpanded((prev) => !prev);
  };

  const handleViewOrder = () => {
    dispatch(setSelectedFormType("view-order"));
  };

  const handleAcceptOrder = () => {
    dispatch(setSelectedFormType("accept-order"));
  };

  const handleRequestRevision = () => {
    dispatch(setSelectedFormType("request-revision"));
  };

  /**
   * 
   * ============================= DATA FETCHING OPERATIONS RELATED FUNCTIONS ======================================================
   * 
   */

  // load more threads
  const loadMoreThreads = () => {
    if (thread?.selectedPage) {
      setPagination({ ...pagination, page: thread?.selectedPage + 1 });
      dispatch(setSelectedPage(null));
    } else {
      setPagination({ ...pagination, page: pagination.page + 1 });
    }
  };


  // if room id and thread id query parameters does not exists
  // refetch latest selected thread object after refresh/close
  const refetchSelectedThreadAndOrder = async () => {
    if (thread.selectedThreadRoomId && !thread.selectedOrder) {
      const result = await doFetchUserOrderByRoomId({
        roomId: thread.selectedThreadRoomId || "",
      });
      if (!result?.success) {
        showToast("error", result?.error?.message);
      } else {
        const suggestedFormType = result?.response?.data?.status === orderStatus.PENDING_PAYMENT ? thread?.selectedFormType === "message" ? "system" : thread?.selectedFormType : thread?.selectedFormType;
        dispatch(setSelectedFormType(suggestedFormType));
      }
    }

    if (
      thread.selectedThreadId &&
      !thread.selectedThread &&
      thread.selectedPage
    ) {
      const result = await doFetchUserThreads({
        page: 0,
        size: pagination.size * thread.selectedPage,
      });
      if (result.success) {
        threasdsRef.current = result?.response?.data?.threads;
        setPagination({
          ...pagination,
          page: result?.response?.data?.threads?.length / SIZE,
        });
        setTotalNumOfRecords(
          result.response?.data?.pagination?.totalDocuments
        );
        for (const element of result?.response?.data?.threads) {
          if (
            element?._id?.toString() === thread?.selectedThreadId?.toString()
          ) {
            dispatch(setSelectedThread(element));
            selectedThreadRef.current = element;
          }
        }
      } else {
        showToast("error", result?.error?.message);
      }
    }
  };

  // if room id and thread id query parameters exists
  // refetch latest selected thread object after refresh/close from query parameters
  const refetchSelectedThreadAndOrderFromQueryParams = async () => {
    // set necessary selected properties
    dispatch(setSelectedThreadId(threadIdSearchParam));
    dispatch(setSelectedThreadRoomId(roomIdSearchParam));

    // fetching the selected order by the room id
    const result = await doFetchUserOrderByRoomId({
      roomId: roomIdSearchParam || "",
    });
    if (result?.success) {
      const suggestedFormType = result?.response?.data?.status === orderStatus.PENDING_PAYMENT ? thread?.selectedFormType === "message" ? "system" : thread?.selectedFormType : thread?.selectedFormType;
      dispatch(setSelectedOrder(result.response?.data));
      dispatch(setSelectedFormType(suggestedFormType));
    } else {
      showToast("error", result?.error?.message);
    }

    // fetching the selected thread
    let res = await doFetchThreadById({ roomId: threadIdSearchParam });
    if (res.success) {
      dispatch(setSelectedThread(res?.response?.data));
      selectedThreadRef.current = res?.response?.data;
    } else {
      showToast("error", res?.error?.message || "Error Occurred!")
    }
  };

  // Fetch all user threads
  const fetchAllUserThreads = async () => {
    try {
      const result = await doFetchUserThreads({
        page: pagination.page,
        size: pagination.size,
      });

      if (result.success) {
        threasdsRef.current = [
          ...thread?.threads,
          ...result.response?.data?.threads,
        ];
        setTotalNumOfRecords(result.response?.data?.pagination?.totalDocuments);

        if (
          thread.selectedOrder &&
          thread.selectedThread &&
          !thread.selectedPage
        ) {
          dispatch(setSelectedPage(pagination.page));
        }
      }

      if (!result.success) {
        showToast("error", result.error?.message);
      }
    } catch {
      showToast("error", "Error occurred during fetching threads.");
    }
  };

  return (
    <div className="px-4 pt-4 flex flex-col lg:flex-row h-screen min-w-[340px] overflow-auto">
      {/* left sidebar */}
      {!roomIdSearchParam && !threadIdSearchParam && (
        <div
          className={`${isScreenLarge
              ? "min-w-[286px] h-screen static overflow-hidden"
              : isSidebarOpen
                ? "absolute w-[286px] h-full translate-x-0 bg-white shadow-lg left-0 top-0  z-50"
                : "absolute w-[286px] overflow-hidden -translate-x-full bg-white left-0 top-0  z-50"
            } flex flex-col items-center max-h- h-full justify-start transition-transform duration-300 ease-in-out`}
        >
          {!isScreenLarge && (
            <button
              className="text-medium-gray pt-8 absolute top-4 right-4"
              onClick={() => setIsSidebarOpen(false)}
            >
              <IoClose className="h-6 w-6 text-primary " />
            </button>
          )}
          <div
            className={`h-full ${isSidebarOpen ? "block overflow-auto" : "block"
              } flex flex-col items-center mt-[24px] `}
          >
            {isScreenLarge && (
              <ArrowButton
                text={`${isScreenSmall ? "Back to Dashboard" : "Back "}`}
                onClick={() => {
                  navigate(-1);
                }}
                width="auto"
                height="44px"
                iconDirection="left"
                iconPosition="before"
                classname="mb-[25px]"
              />
            )}

            {/* Bordered Container */}
            <div
              className={`w-[286px] h-full flex flex-col flex-grow items-center justify-start ${!isScreenLarge
                  ? ""
                  : "border border-medium-gray rounded-[12px] max-h-full"
                }  py-[24px] overflow-hidden`}
            >
              {/* Fixed Topic */}
              <div className="flex flex-col items-start w-full mb-[24px]">
                <div className="flex items-start w-[240px] ml-[24px]">
                  <div className="flex space-x-4 justify-start items-center h-[40px] ">
                    <img src={logo} alt="" className="h-[40px] w-auto" />
                    <h1 className="font-roboto font-semibold text-primary text-[18px] leading-5">
                      Your Orders
                    </h1>
                  </div>
                </div>
              </div>

              {/* Fixed Search Bar */}
              {/* <div className="flex items-center font-inter bg-soft-gray border border-medium-gray rounded-[5px] p-2 w-[240px] mb-[24px]">
                <IoSearch className="text-gray w-6 h-6" />
                <input
                  type="text"
                  placeholder="Search"
                  className="ml-2 w-full bg-transparent text-gray text-[15px] leading-[20px] placeholder-gray focus:outline-none"
                />
              </div> */}

              {/* Scrollable Tutorial Cards Section */}
              {!(isFetchingUserThreads && thread?.threads?.length < 1) &&
                (
                  <div
                    className={`flex flex-wrap gap-4 pt-4 justify-center overflow-y-auto scrollbar-none ${isScreenLarge
                        ? "max-h-[calc(100vh-240px)]"
                        : "max-h-[calc(100vh-52px)]"
                      }`}
                  >
                    {thread?.threads?.map((threadItem, index) => (
                      <TutorialCard
                        key={threadItem?._id}
                        id={threadItem?.roomId}
                        title={threadItem?.subject}
                        // subtitle={`${index}`}
                        count={getUnreadMessagesCount(
                          threadItem,
                          tokenPayload?._id || null
                        )}
                        price={currencyFormatter.format(threadItem?.price || 0) || "$ 00.00"}
                        date={threadItem?.createdAt}
                        status={threadItem?.orderStatus}
                        onClick={() => handleCardClick(threadItem)}
                        isActive={
                          thread?.selectedThreadId?.toString() ===
                          threadItem?._id?.toString()
                        }
                      />
                    ))}
                    {!(!isFetchingUserThreads && thread?.threads?.length < 1) &&
                      (
                        <button
                          onClick={() => {
                            loadMoreThreads();
                          }}
                          className={twMerge(
                            "bg-light-blue text-blue-shades px-[8px] py-[8px] gap-[10px] leading-[19.36px] w-[120px] h-[44px] rounded-[5px] font-inter",
                            thread.threads?.length >= totalNumOfRecords
                              ? "bg-slate-100 text-slate-200"
                              : "bg-light-blue text-blue-shades"
                          )}
                          disabled={thread.threads?.length >= totalNumOfRecords}
                        >
                          {isFetchingUserThreads ? (
                            <div className="flex items-center justify-center">
                              <ClipLoader
                                loading={isFetchingUserThreads}
                                speedMultiplier={0.5}
                                size={22}
                                color="#60a5fa"
                              />
                            </div>
                          ) : (
                            "Load More"
                          )}
                        </button>
                      )
                    }
                  </div>
                )
              }
              {isFetchingUserThreads && thread?.threads?.length < 1 &&
                (
                  <div className="flex flex-col flex-grow justify-center items-center w-full h-[100]">
                    <ClipLoader
                      loading={isFetchingUserThreads && thread?.threads?.length < 1}
                      speedMultiplier={0.5}
                      size={82}
                      color="#60a5fa"
                    />
                  </div>
                )
              }
              {!isFetchingUserThreads && thread?.threads?.length < 1 &&
                (
                  <div className="flex flex-col flex-grow justify-center items-center w-full h-[100]">
                    <div className="flex flex-grow items-center justify-center">
                      <img src={waiting_two} alt="" />
                    </div>
                  </div>
                )
              }
            </div>
          </div>
        </div>
      )}

      {/* Middle Section */}
      <div
        className={`flex flex-col h-full lg:h-auto  flex-grow  mx-[20px] xl:mx-[40px] justify-start mt-0 lg:mt-[24px] mb-[40px]`}
      >
        {/* Back button & thread button */}
        {!isScreenLarge && (
          <div className="flex flex-row z-0 w-full">
            {/* First Button  */}
            {!roomIdSearchParam && !threadIdSearchParam && (
              <button
                className=" sm:flex hidden flex-row items-center justify-center space-x-2 z-50 mb-4 h-[40px] p-2 bg-blue-shades text-white rounded"
                style={{ width: "234px" }} // Set a fixed width for the button
                onClick={() => setIsSidebarOpen(true)}
              >
                <FaRegMessage className="sm:flex hidden h-[24px] w-[24px]" />
                <p className="w-full sm:flex hidden border-l px-2 font-inter text-white font-medium">
                  Open Message Thread
                </p>
              </button>
            )}

            {!roomIdSearchParam && !threadIdSearchParam && (
              <button
                className="flex sm:hidden flex-row  items-center justify-center space-x-4 z-50 mb-4 h-[40px] p-2 bg-blue-shades text-white rounded"
                style={{ width: "40px" }} // Set a fixed width for the button
                onClick={() => setIsSidebarOpen(true)}
              >
                <HiMenu className="flex sm:hidden h-[30px] w-[30px]" />
              </button>
            )}

            {/* Inner Div */}
            <div
              className={twMerge(
                "flex-grow",
                !roomIdSearchParam && !threadIdSearchParam ? "ml-4" : ""
              )}
            >
              <ArrowButton
                text={`${isScreenSmall ? "Back to Dashboard" : "Back "}`}
                onClick={() => {
                  navigate(-1);
                }}
                width="w-full"
                height="40px"
                iconDirection="left"
                iconPosition="before"
                classname="mb-[25px]"
              />
            </div>
          </div>
        )}

        {/* Tab buttons for small screens */}
        <div className="lg:hidden flex flex-row ml-0 mb-0 mt-2 font-roboto text-level-4 ">
          <button
            onClick={() => {
              handleButtonClick("message");
            }}
            className={twMerge(
              `py-2 w-full border border-medium-gray rounded-tl-[5px]`,
              thread?.selectedFormType === "message"
                ? "bg-light-blue text-blue-shades"
                : "text-gray"
            )}
            disabled={thread?.selectedOrder?.status === "PENDING_PAYMENT"}
          >
            Messages
          </button>
          <button
            onClick={() => {
              handleButtonClick("system");
            }}
            className={twMerge(
              `py-2 w-full border  border-medium-gray rounded-tr-[5px]`,
              thread?.selectedFormType !== "message" &&
                thread?.selectedFormType !== "Tab3"
                ? "text-blue-shades bg-light-blue"
                : "text-gray bg-white"
            )}
          >
            Notifications
          </button>
          <button
            onClick={() => {
              handleButtonClick("Tab3");
            }}
            className={twMerge(
              `py-2 w-full border  border-medium-gray rounded-tr-[5px]`,
              thread.selectedFormType === "Tab3"
                ? "text-blue-shades bg-light-blue"
                : "text-gray bg-white"
            )}
          >
            Details
          </button>
        </div>

        {thread?.selectedThread ? (
          <>
            <div className="flex flex-row w-full">
              <div className="flex flex-row">
                {/* back button */}
                {roomIdSearchParam && threadIdSearchParam && (
                  <button
                    className="hidden 1440-br:flex flex-row  items-center justify-center space-x-4 z-50 h-[40px] p-2 mr-3 bg-blue-shades text-white rounded"
                    style={{ width: "40px" }} // Set a fixed width for the button
                    // onClick={() => setIsSidebarOpen(true)}
                    onClick={() => {
                      navigate(-1);
                    }}
                  >
                    <IoMdArrowRoundBack className="flex h-[30px] w-[30px]" />
                  </button>
                )}
              </div>
              <div className="flex flex-col w-full m-0 p-0">
                <div className="flex flex-row w-full ">
                  {/* Topic */}
                  <h2
                    className={`hidden lg:block font-roboto font-semibold text-primary text-level-2 leading-[20px] ${isScreenLarge ? "" : "ml-0"
                      }`}
                  >
                    {thread?.selectedThread?.roomId} <span> - </span>
                    {thread?.selectedOrder?.orderRequirements?.subject}{" "}
                    <span> </span>
                  </h2>
                </div>

                {/* Order Status */}
                <div
                  className={`hidden lg:flex flex-row items-center space-x-2 mb-[22px] mt-2 ${isScreenLarge ? "" : "ml-0"
                    }`}
                >
                  <div
                    className={`w-3 h-3 rounded-[29.27px] ${[
                        orderStatus.PENDING_PAYMENT,
                        orderStatus.IN_REVISION,
                        orderStatus.COMPLAINT_RAISED,
                      ].includes(thread?.selectedOrder?.status)
                        ? "bg-light-red"
                        : thread?.selectedOrder?.status ===
                          orderStatus.ORDER_IN_PROGRESS
                          ? "bg-[#06BCCC]"
                          : [
                            orderStatus.ORDER_DELIVERED,
                            orderStatus.COMPLETED,
                            orderStatus.RESOLVED,
                          ].includes(thread?.selectedOrder?.status)
                            ? "bg-blue-500"
                            : "bg-gray-500" // Default color if no status matches
                      } `}
                  ></div>
                  <p
                    className={` font-inter font-medium text-secondary text-level-5 leading-3`}
                  >
                    {formatOrderStatus(thread?.selectedOrder?.status)}
                  </p>
                  <div className="flex flex-row flex-grow"></div>
                  {/* toggle button for system and normal messages */}

                  <div className=" hidden lg:flex items-center relative font-inter bg-soft-blue border border-blue-shades rounded-full w-[180px] h-[28px] font-[500]">
                    {/* Sliding background for active form */}
                    <div
                      className={`absolute rounded-full h-[28px] transition-all duration-300 ${thread?.selectedFormType === "message"
                          ? "bg-blue-shades left-0 w-[80px]"
                          : "bg-blue-shades left-[80px] w-[100px]"
                        }`}
                    ></div>

                    {/* Message Button */}
                    <button
                      onClick={() => handleButtonClick("message")}
                      className={twMerge(
                        `z-10 w-[80px] h-[23px] text-[12px] leading-[14.45px] cursor-pointer`,
                        thread?.selectedFormType === "message"
                          ? "text-white"
                          : "text-secondary"
                      )}
                      disabled={
                        thread?.selectedOrder?.status === "PENDING_PAYMENT"
                      }
                    >
                      Message
                    </button>

                    {/* Notifications Button */}
                    <button
                      onClick={() => handleButtonClick("system")}
                      className={twMerge(
                        `z-10 w-[100px] h-[23px] text-[12px] leading-[14.45px] cursor-pointer`,
                        thread?.selectedFormType !== "message"
                          ? "text-white"
                          : "text-secondary"
                      )}
                    >
                      Notifications
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {isLargeScreen || thread?.selectedFormType === "message" ? (
              <>
                <div className="flex relative border flex-grow border-medium-gray rounded-b-[12px] lg:rounded-[12px] p-4 overflow-hidden mb-12 lg:mb-0">
                  {/* Scrollable Messages Container */}
                  <div
                    className="h-[100%] w-full overflow-auto scrollbar-none "
                    style={{ maxHeight: "calc(100% - 0px)" }}
                  >
                    <MessageList
                      socket={socket}
                      roleRef={roleRef}
                      userIdRef={userIdRef}
                      selectedThreadRef={selectedThreadRef}
                      threasdsRef={threasdsRef}
                      chatRef={chatRef}
                      isFetchUserOrderByRoomId={isFetchUserOrderByRoomId}
                      isFetchThreadById={isFetchThreadById}
                      roomIdSearchParam={roomIdSearchParam}
                      threadIdSearchParam={threadIdSearchParam}
                      refetchSelectedThreadAndOrderFromQueryParams={refetchSelectedThreadAndOrderFromQueryParams}
                      refetchSelectedThreadAndOrder={refetchSelectedThreadAndOrder}
                    />
                  </div>
                </div>{" "}
              </>
            ) : thread?.selectedFormType !== "message" &&
              thread?.selectedFormType !== "Tab3" ? (
              <>
                <div
                  className="overflow-auto scrollbar-none border border-medium-gray"
                  style={{ maxHeight: "calc(100% - 0px)" }}
                >
                  <MessageList
                    socket={socket}
                    roleRef={roleRef}
                    userIdRef={userIdRef}
                    selectedThreadRef={selectedThreadRef}
                    threasdsRef={threasdsRef}
                    chatRef={chatRef}
                    isFetchUserOrderByRoomId={isFetchUserOrderByRoomId}
                    isFetchThreadById={isFetchThreadById}
                    roomIdSearchParam={roomIdSearchParam}
                    threadIdSearchParam={threadIdSearchParam}
                    refetchSelectedThreadAndOrderFromQueryParams={refetchSelectedThreadAndOrderFromQueryParams}
                    refetchSelectedThreadAndOrder={refetchSelectedThreadAndOrder}
                  />
                </div>
              </>
            ) : (
              <>
                <div className="h-full border border-medium-gray rounded-tr-[12px] rounded-b-[12px] lg:rounded-[12px] p-4 mb-12 lg:mb-0 overflow-auto scrollbar-none">
                  <div className="mt-[22px]">
                    {![
                      // orderStatus.ORDER_DELIVERED,
                      orderStatus.RESOLVED,
                      orderStatus.COMPLETED,
                      orderStatus.CANCELLED,
                      orderStatus.PENDING_PAYMENT,
                      orderStatus.COMPLAINT_RAISED,
                    ].includes(thread?.selectedOrder?.status) && (
                        <CountdownTimer
                          deliveryDate={
                            thread?.selectedOrder?.orderRequirements?.deadline
                          }
                          status={thread?.selectedOrder?.status}
                          handleAcceptOrder={handleAcceptOrder}
                          handleRequestRevision={handleRequestRevision}
                        />
                      )}
                  </div>

                  <OrderDetails
                    tutorial={{
                      ...thread?.selectedThread,
                      deadline:
                        thread?.selectedOrder?.orderRequirements?.deadline,
                    }}
                    isExpanded={isExpanded}
                    toggleSection={toggleSection}
                    handleViewOrder={handleViewOrder}
                  />

                  {thread?.selectedThread &&
                    [
                      orderStatus.ORDER_DELIVERED,
                      orderStatus.COMPLETED,
                      orderStatus.COMPLAINT_RAISED,
                      orderStatus.IN_REVISION,
                      orderStatus.RESOLVED,
                    ].includes(thread?.selectedOrder?.status) && (
                      <FinalSubmission
                        selectedOrder={thread?.selectedOrder}
                      />
                    )}

                  {thread?.selectedThread && (
                    <TrackOrder
                      trackOrder={thread?.selectedOrder?.orderProgress}
                      currentState={thread?.selectedOrder?.status}
                    />
                  )}
                </div>
              </>
            )}
          </>
        ) : (
          <>
            {/* Default view */}
            <div className="flex flex-col h-full">
              <p
                className={` ${isScreenLarge ? "" : ""
                  } hidden lg:block font-roboto font-semibold text-primary text-level-2 leading-[20px] mb-[48px] `}
              >
                Select your Order
              </p>
              <div className="border flex-grow  flex-col flex items-center text-center justify-center border-medium-gray rounded-b-[12px] p-4 relative overflow-auto scrollbar-webkit ">
                {!thread?.selectedOrder && isFetchingUserThreads &&
                  <>
                    <div className="flex items-center justify-center w-full h-[100%]">
                      <ClipLoader
                        loading={true}
                        speedMultiplier={0.5}
                        size={82}
                        color="#60a5fa"
                      />
                    </div>
                  </>
                }
                {!roomIdSearchParam && !threadIdSearchParam && !isFetchingUserThreads &&
                  <>
                    <img src={waiting} alt="" />
                    <p className="font-roboto font-semibold text-primary text-level-2 leading-[20px] mb-[24px] ">
                      Select one order to continue
                    </p>
                    <p className="font-inter font-normal text-level-4 text-gray max-w-[376px]">
                      Fill the catalog with Orders by adding your assignments. All
                      Orders are posted anonymously.
                    </p>
                    <ArrowButton
                      text="Do My Paper"
                      onClick={() => { navigate("/calculation") }}
                      width="auto"
                      height="44px"
                      iconDirection="right"
                      iconPosition="after"
                      classname="mt-[24px]"
                    />
                  </>
                }
              </div>
            </div>
          </>
        )}
      </div>

      {/* right Section */}
      <div className=" flex-col lg:block min-w-[286px] max-w-[286px] hidden justify-start items-center mt-[24px] border mb-[40px] border-medium-gray rounded-[5px] overflow-y-auto scrollbar-none pb-6">
        <div className="">
          {" "}
          {thread?.selectedThread ? (
            <>
              <div className="mt-[22px]">
                {![
                  // orderStatus.ORDER_DELIVERED,
                  orderStatus.RESOLVED,
                  orderStatus.COMPLETED,
                  orderStatus.CANCELLED,
                  orderStatus.PENDING_PAYMENT,
                  orderStatus.COMPLAINT_RAISED,
                ].includes(thread?.selectedOrder?.status) && (
                    <CountdownTimer
                      deliveryDate={
                        thread?.selectedOrder?.orderRequirements?.deadline
                      }
                      status={thread?.selectedOrder?.status}
                      handleAcceptOrder={handleAcceptOrder}
                      handleRequestRevision={handleRequestRevision}
                    />
                  )}
              </div>

              <OrderDetails
                tutorial={{
                  ...thread?.selectedThread,
                  deadline: thread?.selectedOrder?.orderRequirements?.deadline,
                }}
                isExpanded={isExpanded}
                toggleSection={toggleSection}
                handleViewOrder={handleViewOrder}
              />

              {thread?.selectedThread &&
                [
                  orderStatus.ORDER_DELIVERED,
                  orderStatus.COMPLETED,
                  orderStatus.COMPLAINT_RAISED,
                  orderStatus.IN_REVISION,
                  orderStatus.RESOLVED,
                ].includes(thread?.selectedOrder?.status) && (
                  <FinalSubmission
                    selectedOrder={thread?.selectedOrder}
                  />
                )}

              {thread?.selectedThread && (
                <TrackOrder
                  trackOrder={thread?.selectedOrder?.orderProgress}
                  currentState={thread?.selectedOrder?.status}
                />
              )}
            </>
          ) : (
            <div className="flex flex-col relative w-full">
              <h1 className="absolute top-4 left-4 font-roboto font-semibold text-[18px] leading-5 text-primary ">
                Order Details
              </h1>
              <div className="flex flex-grow items-center justify-center pt-[100px] md:pt-[160px] lg:pt-[200px] 2xl:pt-[260px]">
                <img src={waiting_two} alt="" />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Thread;
